import { useEffect } from 'react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const privacyData = {
    "company": "Vital Hive Technologies",
    "document_title": "Privacy Policy",
    "last_updated": "August 06, 2025",
    "company_names": [
      "Vital Hive Technologies LLC",
      "Vital Hive",
      "VHT",
      "Vital Hive Clinician App"
    ],
    "websites": [
      "vitalhivetechnologies.com",
      "www.bluewaterhealthhcm.com"
    ],
    "platforms": [
      "VHT Facility Portal",
      "VHT Admin Portal",
      "VHT mobile application (VHT Clinician)"
    ],
    "applies_to": [
      "Our website",
      "VHT Facility Portal",
      "VHT Admin Portal",
      "VHT mobile application (VHT Clinician)",
      "All related services, technology, features, and content available through the above (collectively, the \"Platform\")"
    ],
    "agreement_clause": "By accessing or using the Platform, you agree to this Privacy Policy. If you do not agree, please do not access or use the Services.",
    "sections": {
      "1": {
        "title": "Information We Collect",
        "subsections": {
          "1.1": {
            "title": "Information You Provide",
            "content": "We collect personal information you voluntarily provide when you:",
            "activities": [
              "Register or create an account",
              "Complete credentialing or scheduling forms",
              "Communicate with Facilities or our support team",
              "Apply for shifts or respond to messages"
            ],
            "may_include": [
              "Name, contact details (email, phone, address), username/password",
              "Date of birth, gender, and demographic details",
              "Professional credentials, licenses, certifications, SSN, and government-issued IDs",
              "Employment eligibility documentation",
              "Profile photos and resumes",
              "Bank account or payment details",
              "Communications, inquiries, and support tickets",
              "Any content you upload or submit"
            ],
            "note": "Any documents you upload (e.g., licenses, forms) are not redacted by Vital Hive. Please redact sensitive information prior to uploading if desired."
          },
          "1.2": {
            "title": "For Providers",
            "content": "If you are a healthcare Provider, we may collect:",
            "items": [
              "Background check authorizations",
              "Work availability and scheduling preferences",
              "Calendar access (if enabled)",
              "Location data (for time-tracking, invoicing, and shift verification)",
              "Messages to and from Facilities",
              "Ratings, reviews, or performance feedback"
            ]
          },
          "1.3": {
            "title": "For Facilities",
            "content": "If you are a Facility user:",
            "items": [
              "Name, job title, and contact information",
              "Facility name, type, and licensing details",
              "Shift posting details",
              "Messages with Providers",
              "Scheduling and billing records",
              "Survey or support responses"
            ]
          },
          "1.4": {
            "title": "Automatically Collected Information",
            "content": "We use cookies and other technologies to collect:",
            "items": [
              "Device type, OS, and browser",
              "IP address and general geolocation",
              "Usage metrics (pages viewed, session times, link clicks)",
              "Referring/exit URLs",
              "Error logs and language preferences"
            ],
            "consent_note": "With your consent (via device settings), we may collect precise location data for shift check-in/out and digital invoicing."
          },
          "1.5": {
            "title": "Cookies and Analytics",
            "content": "We use cookies and similar technologies to understand user behavior, save preferences and settings, improve site performance, and support marketing efforts (where permitted).",
            "cookie_uses": [
              "Authenticate users",
              "Maintain session preferences",
              "Track usage for security and platform improvement",
              "Deliver targeted advertising (subject to your choices)"
            ],
            "analytics_note": "We use third-party analytics (e.g., Google Analytics)."
          }
        }
      },
      "2": {
        "title": "How We Use Your Information",
        "content": "We use your information to:",
        "subsections": {
          "2.1": {
            "title": "Operate the Platform",
            "items": [
              "Create and manage user accounts",
              "Match Providers with Facility shifts",
              "Communicate shift requests, confirmations, or changes",
              "Enable invoicing, payment, and support functions",
              "Display Provider profiles and Facility details",
              "Respond to inquiries and provide customer support"
            ]
          },
          "2.2": {
            "title": "Maintain Trust and Safety",
            "items": [
              "Verify user identity, licensing, and credentials",
              "Conduct background checks (where legally permissible)",
              "Prevent fraud, abuse, and prohibited conduct",
              "Monitor and enforce compliance with our Terms of Service"
            ]
          },
          "2.3": {
            "title": "Improve and Promote Our Services",
            "items": [
              "Understand usage trends and develop new features",
              "Personalize your experience",
              "Send surveys and collect feedback",
              "Send promotional or service-related communications"
            ]
          },
          "2.4": {
            "title": "Comply with Legal Obligations",
            "items": [
              "Respond to law enforcement or regulatory requests",
              "Enforce our contracts and rights",
              "Maintain audit, tax, or legal records"
            ]
          }
        }
      },
      "3": {
        "title": "How We Share Information",
        "content": "We may share your information with:",
        "subsections": {
          "3.1": {
            "title": "With Consent or At Your Direction",
            "items": [
              "Sharing Provider profiles and credentials with Facilities",
              "Enabling Facilities to verify shift attendance via location tracking"
            ]
          },
          "3.2": {
            "title": "Service Providers",
            "items": [
              "Credentialing and background check vendors",
              "Cloud hosting, payment processors, and customer support platforms",
              "Communication providers and analytics vendors"
            ],
            "note": "All vendors are bound by confidentiality and data protection obligations."
          },
          "3.3": {
            "title": "Third-Party Advertising",
            "items": [
              "Certain non-sensitive data may be shared for targeted ads. You can opt out via \"Your Privacy Choices\" in the website site footer or in the VHT Clinician app."
            ]
          },
          "3.4": {
            "title": "Legal and Regulatory Compliance",
            "items": [
              "To comply with subpoenas, court orders, and law enforcement",
              "To protect Vital Hive, users, or the public from harm or fraud"
            ]
          },
          "3.5": {
            "title": "Business Transfers",
            "items": [
              "In the event of a merger, acquisition, asset sale, bankruptcy, or reorganization, your information may be transferred."
            ]
          }
        }
      },
      "4": {
        "title": "Data Security",
        "content": "We implement technical and organizational safeguards to protect your data. These include encryption, access controls, and audit logging. However, no system is completely secure. If you believe your interaction with us is no longer secure, please contact us immediately.",
        "user_responsibility": "You are responsible for maintaining the confidentiality of your account credentials."
      },
      "5": {
        "title": "Your Rights and Choices",
        "subsections": {
          "5.1": {
            "title": "Managing Your Account",
            "content": "You can update, correct, or delete profile information through your dashboard. To request account deletion, contact us at support@vitalhivetechnologies.com."
          },
          "5.2": {
            "title": "Marketing Preferences",
            "content": "You can opt out of promotional emails by using the \"unsubscribe\" link or by contacting us at support@vitalhivetechnologies.com. You may still receive essential service notifications."
          },
          "5.3": {
            "title": "Privacy Rights by Jurisdiction",
            "content": "Depending on your location, you may have rights under applicable law, such as:",
            "rights": [
              {
                "name": "Access",
                "description": "Requesting a copy of the personal information we hold about you."
              },
              {
                "name": "Rectification",
                "description": "Requesting the correction of any inaccurate or incomplete personal information."
              },
              {
                "name": "Erasure (Right to be Forgotten)",
                "description": "Requesting the deletion of your personal information under certain circumstances."
              },
              {
                "name": "Objection",
                "description": "Objecting to the processing of your personal information in certain situations."
              },
              {
                "name": "Portability",
                "description": "Requesting that we provide you with your personal information in a structured, commonly used, and machine-readable format."
              }
            ],
            "request_process": "Submit requests to support@vitalhivetechnologies.com. We will verify your identity before fulfilling them. Some information may be exempt from deletion under law (e.g., transaction history, legal obligations)."
          },
          "5.4": {
            "title": "Do Not Sell or Share",
            "content": "You may opt out of the \"sale\" or \"sharing\" of personal information for targeted advertising purposes by visiting Your Privacy Choices on our website."
          }
        }
      },
      "6": {
        "title": "Data Retention",
        "content": "We retain your information:",
        "items": [
          "As long as necessary to provide the Services",
          "To comply with legal obligations",
          "For audit, fraud prevention, and dispute resolution"
        ],
        "note": "Inactive or deleted accounts may retain archival data in backup systems for a limited time."
      },
      "7": {
        "title": "Third-Party Links",
        "content": "Our Platform may link to third-party websites or services. This Privacy Policy does not apply to those external services. Please review their privacy practices independently."
      },
      "8": {
        "title": "Children's Privacy",
        "content": "The Platform is not intended for children under 13. We do not knowingly collect personal information from children. If you believe we have done so, please contact us for deletion."
      },
      "9": {
        "title": "International Users",
        "content": "Our Platform is hosted in the United States. By using our Services, you acknowledge that your personal information will be transferred to, stored in, and processed in the U.S., where data protections may differ from your home country."
      },
      "10": {
        "title": "Changes to this Privacy Policy",
        "content": "We may update this Privacy Policy periodically. If we make material changes, we will notify you by updating the \"Last Updated\" date and providing notice via the Platform or email. Your continued use of the Services indicates acceptance of the changes."
      },
      "11": {
        "title": "Contact Us",
        "content": "For questions, concerns, or privacy requests:",
        "company_name": "Vital Hive Technologies LLC",
        "email": "support@vitalhivetechnologies.com",
        "address": "3950 Sunforest Ct. Suite 200 Toledo, Ohio 43623"
      },
      "12": {
        "title": "Reminder",
        "content": "By using our Services, you acknowledge that you have read and understood this Privacy Policy and consent to our data practices as described herein. For more information about the rules and guidelines governing the use of our website and services, please consult our Terms of Service ."
      }
    }
  };

  const renderListItems = (items: string[]) => (
    <ul className="list-disc list-inside space-y-2 ml-6 text-gray-700">
      {items.map((item, index) => (
        <li key={index} className="text-base leading-relaxed">{item}</li>
      ))}
    </ul>
  );

  const renderRightsList = (rights: any[]) => (
    <ul className="list-disc list-inside space-y-3 ml-6 text-gray-700">
      {rights.map((right, index) => (
        <li key={index} className="text-base leading-relaxed">
          <strong>{right.name}:</strong> {right.description}
        </li>
      ))}
    </ul>
  );

  const renderSubsection = (subsection: any, subsectionKey: string) => (
    <div key={subsectionKey} className="mb-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-3">
        {subsectionKey} {subsection.title}
      </h4>
      <p className="text-gray-700 leading-relaxed mb-3">
        {subsection.content}
      </p>
      
      {subsection.activities && renderListItems(subsection.activities)}
      {subsection.items && renderListItems(subsection.items)}
      {subsection.may_include && renderListItems(subsection.may_include)}
      {subsection.cookie_uses && renderListItems(subsection.cookie_uses)}
      {subsection.rights && renderRightsList(subsection.rights)}
      
      {subsection.note && (
        <p className="text-gray-600 text-sm italic mt-3 p-3 bg-gray-50 rounded">
          <strong>Note:</strong> {subsection.note}
        </p>
      )}
      
      {subsection.consent_note && (
        <p className="text-gray-600 text-sm italic mt-3 p-3 bg-gray-50 rounded">
          <strong>Consent:</strong> {subsection.consent_note}
        </p>
      )}
      
      {subsection.analytics_note && (
        <p className="text-gray-600 text-sm italic mt-3 p-3 bg-gray-50 rounded">
          <strong>Analytics:</strong> {subsection.analytics_note}
        </p>
      )}
      
      {subsection.request_process && (
        <p className="text-gray-600 text-sm italic mt-3 p-3 bg-gray-50 rounded">
          <strong>Request Process:</strong> {subsection.request_process}
        </p>
      )}
    </div>
  );

  const renderSection = (section: any, sectionKey: string) => (
    <div key={sectionKey} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
      <h3 className="text-xl font-bold text-gray-900 mb-4">
        {sectionKey}. {section.title}
      </h3>
      
      {section.content && (
        <p className="text-gray-700 leading-relaxed mb-4">
          {section.content}
        </p>
      )}
      
      {section.user_responsibility && (
        <p className="text-gray-600 text-sm italic mt-3 p-3 bg-gray-50 rounded">
          <strong>User Responsibility:</strong> {section.user_responsibility}
        </p>
      )}
      
      {section.items && renderListItems(section.items)}
      
      {section.note && (
        <p className="text-gray-600 text-sm italic mt-3 p-3 bg-gray-50 rounded">
          <strong>Note:</strong> {section.note}
        </p>
      )}
      
      {section.subsections && (
        <div className="mt-6">
          {Object.entries(section.subsections).map(([subsectionKey, subsection]) =>
            renderSubsection(subsection, subsectionKey)
          )}
        </div>
      )}
      
      {section.company_name && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-gray-700 mb-2">
            <strong>Company:</strong> {section.company_name}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>Email:</strong> <a href={`mailto:${section.email}`} className="text-blue-600 hover:underline">{section.email}</a>
          </p>
          <p className="text-gray-700">
            <strong>Address:</strong> {section.address}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Document Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {privacyData.company}
            </h1>
            <h2 className="text-xl font-semibold text-blue-600 mb-2">
              {privacyData.document_title}
            </h2>
            <p className="text-gray-600 text-base">
              Last Updated: {privacyData.last_updated}
            </p>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              {privacyData.company_names[0]} and its affiliates respect your privacy and are committed to protecting the personal information you share with us.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Privacy Policy explains how {privacyData.company_names[1]} collects, uses, discloses, and safeguards your information when you visit our website, {privacyData.platforms.join(', ')}, or use our services (collectively, the "Services").
            </p>
            <div className="mb-4">
              <p className="text-gray-700 font-medium mb-2">This policy applies to:</p>
              {renderListItems(privacyData.applies_to)}
            </div>
            <p className="text-gray-700 leading-relaxed font-medium">
              {privacyData.agreement_clause}
            </p>
          </div>
        </div>

        {/* Sections */}
        {Object.entries(privacyData.sections).map(([sectionKey, section]) =>
          renderSection(section, sectionKey)
        )}

        {/* Document Footer */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Last updated: {privacyData.last_updated}
            </p>
            <p className="text-gray-500 text-xs mt-2">
              This document outlines our commitment to protecting your privacy and personal information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
